
/**
 * Canvas Performance Monitor
 *
 * Lightweight FPS, render time, and memory tracker for React Flow canvas.
 * Usage: Call `CanvasPerformanceMonitor.start()` to begin tracking,
 * `CanvasPerformanceMonitor.report()` to get stats, `CanvasPerformanceMonitor.stop()` to end.
 *
 * Available on `window.__canvasPerf` in development for console debugging.
 */

interface PerformanceSnapshot {
  fps: number
  avgFrameTime: number
  maxFrameTime: number
  nodeCount: number
  edgeCount: number
  memoryMB: number | null
  timestamp: number
}

interface PerformanceReport {
  avgFps: number
  minFps: number
  maxFps: number
  avgFrameTime: number
  maxFrameTime: number
  p95FrameTime: number
  memoryMB: number | null
  nodeCount: number
  edgeCount: number
  duration: number
  samples: number
}

class CanvasPerformanceMonitorImpl {
  private rafId: number | null = null
  private frames: number[] = []
  private lastFrameTime = 0
  private snapshots: PerformanceSnapshot[] = []
  private startTime = 0
  private running = false

  start() {
    if (this.running) return
    this.running = true
    this.frames = []
    this.snapshots = []
    this.startTime = performance.now()
    this.lastFrameTime = performance.now()
    this.tick()
  }

  stop() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
    this.running = false
  }

  report(): PerformanceReport | null {
    if (this.snapshots.length === 0) return null

    const fpsList = this.snapshots.map((s) => s.fps)
    const frameTimes = this.frames.filter((t) => t > 0).sort((a, b) => a - b)

    const avgFps = fpsList.reduce((a, b) => a + b, 0) / fpsList.length
    const minFps = Math.min(...fpsList)
    const maxFps = Math.max(...fpsList)
    const avgFrameTime =
      frameTimes.length > 0
        ? frameTimes.reduce((a, b) => a + b, 0) / frameTimes.length
        : 0
    const maxFrameTime = frameTimes.length > 0 ? frameTimes[frameTimes.length - 1] : 0
    const p95Index = Math.floor(frameTimes.length * 0.95)
    const p95FrameTime = frameTimes.length > 0 ? frameTimes[p95Index] || maxFrameTime : 0

    const lastSnapshot = this.snapshots[this.snapshots.length - 1]

    const report: PerformanceReport = {
      avgFps: Math.round(avgFps * 10) / 10,
      minFps: Math.round(minFps * 10) / 10,
      maxFps: Math.round(maxFps * 10) / 10,
      avgFrameTime: Math.round(avgFrameTime * 100) / 100,
      maxFrameTime: Math.round(maxFrameTime * 100) / 100,
      p95FrameTime: Math.round(p95FrameTime * 100) / 100,
      memoryMB: lastSnapshot.memoryMB,
      nodeCount: lastSnapshot.nodeCount,
      edgeCount: lastSnapshot.edgeCount,
      duration: Math.round((performance.now() - this.startTime) / 1000),
      samples: this.snapshots.length,
    }

    return report
  }

  private tick = () => {
    const now = performance.now()
    const delta = now - this.lastFrameTime
    this.lastFrameTime = now
    this.frames.push(delta)

    // Snapshot every second
    if (this.frames.length % 60 === 0) {
      this.takeSnapshot()
    }

    if (this.running) {
      this.rafId = requestAnimationFrame(this.tick)
    }
  }

  private takeSnapshot() {
    const recentFrames = this.frames.slice(-60)
    const avgFrameTime =
      recentFrames.reduce((a, b) => a + b, 0) / recentFrames.length
    const fps = 1000 / avgFrameTime

    const nodeCount = document.querySelectorAll('.react-flow__node').length
    const edgeCount = document.querySelectorAll('.react-flow__edge').length

    let memoryMB: number | null = null
    if ('memory' in performance) {
      const mem = (performance as unknown as { memory: { usedJSHeapSize: number } }).memory
      memoryMB = Math.round((mem.usedJSHeapSize / 1024 / 1024) * 10) / 10
    }

    this.snapshots.push({
      fps: Math.round(fps * 10) / 10,
      avgFrameTime: Math.round(avgFrameTime * 100) / 100,
      maxFrameTime: Math.round(Math.max(...recentFrames) * 100) / 100,
      nodeCount,
      edgeCount,
      memoryMB,
      timestamp: performance.now(),
    })
  }
}

export const CanvasPerformanceMonitor = new CanvasPerformanceMonitorImpl()

// Expose on window in development
if (typeof window !== 'undefined') {
  ;(window as unknown as { __canvasPerf: typeof CanvasPerformanceMonitor }).__canvasPerf = CanvasPerformanceMonitor
}
